import { Injectable } from '@angular/core';
import { List } from '../constants-types/PiList.model';
import {
    StrategyQuadrant,
    AgreementStrategyItem,
    AgreementItemScore,
    StrategyQuadrantAlignmentMap,
    strategyQuadrantQuestionMap,
    StrategyQuadrantEmphasis,
    PizzaConfigType,
    DerivedTeamName,
    BehavioralEmphasisScores,
    TeamConfigResult,
    IStrategyOrTeamSelection
} from '../constants-types/StrategyConstants';

@Injectable({
    providedIn: 'root'
})
export class StrategyScoringUtilities {
    quadrants = [
        StrategyQuadrant.Cultivating,
        StrategyQuadrant.Exploring,
        StrategyQuadrant.Producing,
        StrategyQuadrant.Stabilizing
    ];

    strategyItemIsPursued(strategyItem: AgreementStrategyItem): boolean {
        const pursueCount = strategyItem.scores.get(AgreementItemScore.Pursue);
        const doNotPursueCount = strategyItem.scores.get(
            AgreementItemScore.DoNotPursue
        );
        const totalCount = pursueCount + doNotPursueCount;
        return pursueCount / totalCount > 2 / 3;
    }

    getAlignmentFromFinalizedSelections(
        selectedStrategyItems: List<IStrategyOrTeamSelection>
    ): StrategyQuadrantAlignmentMap {
        const strategyQuadrantAlignmentMap = new Map();

        this.quadrants.forEach((quadrant) => {
            const quadrantPursuedStrategies = selectedStrategyItems.filter(
                (strategyItem) => {
                    const subQuestionId = strategyItem.id;
                    return strategyQuadrantQuestionMap
                        .get(quadrant)
                        .includes(subQuestionId);
                }
            );
            const value =
                quadrantPursuedStrategies.size / selectedStrategyItems.size ||
                0;
            let emphasis = StrategyQuadrantEmphasis.None;
            if (value >= 0.3) {
                emphasis = StrategyQuadrantEmphasis.High;
            } else if (value >= 0.2 && value < 0.3) {
                emphasis = StrategyQuadrantEmphasis.Medium;
            } else if (value > 0 && value < 0.2) {
                emphasis = StrategyQuadrantEmphasis.Low;
            }
            const displayValueInput = value * 100;
            const displayValue =
                displayValueInput !== 100 && displayValueInput !== 0
                    ? displayValueInput.toPrecision(2)
                    : displayValueInput.toString();

            strategyQuadrantAlignmentMap.set(quadrant, {
                displayValue,
                emphasis
            });
        });
        return strategyQuadrantAlignmentMap;
    }

    getTeamConfiguration(
        behavioralScores: BehavioralEmphasisScores,
        configType?: PizzaConfigType
    ): TeamConfigResult {
        // Go through all four quadrant's score and get only the high scoring ones
        const highScoreQuadrants: StrategyQuadrant[] = [];
        behavioralScores.forEach((value, key) => {
            if (value === StrategyQuadrantEmphasis.High) {
                highScoreQuadrants.push(key);
            }
        });
        return {
            quadrants: highScoreQuadrants,
            teamName: this.getTeamName(highScoreQuadrants, configType)
        };
    }

    // Based on https://app.predictiveindex.com/Assets/Design/TeamConfigurations.pdf
    getTeamName(
        quadrants: StrategyQuadrant[],
        configType?: PizzaConfigType
    ): DerivedTeamName {
        // if no high scoring ones return Adapting team
        if (quadrants.length === 0) {
            return DerivedTeamName.Adapting;
        } else if (quadrants.length === 1) {
            // if only one quadrant has high score then return that quadrant
            return DerivedTeamName[StrategyQuadrant[quadrants[0]]];
        } else if (quadrants.length === 3) {
            // if thee quaratns have a high emphasis return adapting (this is only possible in strategy configs)
            return DerivedTeamName.Adapting;
        } else {
            if (
                quadrants.includes(StrategyQuadrant.Cultivating) &&
                quadrants.includes(StrategyQuadrant.Stabilizing)
            ) {
                return DerivedTeamName.Anchoring;
            } else if (
                quadrants.includes(StrategyQuadrant.Cultivating) &&
                quadrants.includes(StrategyQuadrant.Exploring)
            ) {
                return DerivedTeamName.Bolstering;
            } else if (
                quadrants.includes(StrategyQuadrant.Stabilizing) &&
                quadrants.includes(StrategyQuadrant.Producing)
            ) {
                return DerivedTeamName.Executing;
            } else if (
                quadrants.includes(StrategyQuadrant.Exploring) &&
                quadrants.includes(StrategyQuadrant.Producing)
            ) {
                return DerivedTeamName.Pathfinding;
            } else if (
                quadrants.includes(StrategyQuadrant.Cultivating) &&
                quadrants.includes(StrategyQuadrant.Producing)
            ) {
                if (configType === 'strat') {
                    return DerivedTeamName.CultivatingAndProducing;
                } else if (configType === 'team') {
                    return DerivedTeamName.Adapting;
                }
            } else if (
                quadrants.includes(StrategyQuadrant.Stabilizing) &&
                quadrants.includes(StrategyQuadrant.Exploring)
            ) {
                if (configType === 'strat') {
                    return DerivedTeamName.ExploringAndStabilizing;
                } else if (configType === 'team') {
                    return DerivedTeamName.Adapting;
                }
            }
        }
    }
}
